<template>
    <div
        class="era-root"
        @click.stop="(ev)=>{onEraClick(ev)}"
        @keydown.enter="onEraClick()"
    >
        <div
            ref="eraSubroot"
            class="era-subroot"
            :style="`left: ${startX-cameraStartX}px;`"
        >
            <div
                :id="`eraBar${nodeid}`"
                ref="eraBar" 
                role="button"
                :tabindex="tabIndex" 
                :aria-label="`Era: ${name}, from ${dateStart.getUTCBCString(node.dateOptions)} to ${dateEnd.getUTCBCString(node.dateOptions)}`" 
                class="era-bar" 
                :style="`background-color: ${bgColor}; width: ${tailLength}px; height: ${rootHeight + 20}px`"
                @focus="$emit('scrolltonode', node)"
            >
                <!--<div ref="eraBar" class="era-bar" :style="`background-color: ${bgColor}; width: ${tailLength}px; height: ${rootHeight + 20}px`">-->
                <div
                    class="era-text-container"
                    :style="`padding-left: ${textBounds.left}px; padding-right: ${textBounds.right}px; height: 100%; width: 100%;`"
                >
                    <div
                        ref="eraText"
                        :style="`width: 100%; padding-left: ${textLeftPadding}px; padding-right: ${textRightPadding}px;`"
                        class="era-text noselect nopointerevents"
                        @click.stop="()=>{}"
                    >
                        <div style="width: 100%;height: 100%;">
                            <span ref="eraActualText">
                                {{ name }}
                                <!-- {{Math.round(edgeBounds.left)}} /
								[{{Math.round(textBounds.left)}}, {{Math.round(textBounds.right)}}]-->
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import Node from '@/models/Node';
import Vector2 from '@/models/Vector2';
import Mutations from '@/store/mutations';
import rgb from 'hsv-rgb';
import Camera from '@/models/DOMCamera';
import CalculateTextSize from '@/util/TextSize';

// Fits text to container
import fitty from 'fitty';
import ColorUtility from '../../util/ColorUtility';

function clamp(v, min, max)
{
    return Math.min(Math.max(v, min), max);
}

export default Vue.extend({
    refs: ['eraSubroot', 'eraText', 'eraBar', 'eraActualText'],
    props: 
	{
	    nodeid : Number,
	    camera : Camera,
	    trackCnt : Number,
	    rootHeight : Number,
	    tabIndex : Number,
	    isCameraDragging : Boolean,
	    eras : Array
	},
    data()
    {
        return {
            fitty: null,
            // Switches for improved reactivity
            _fPosUpdate: false,
            _fDateUpdate: false,
			
            // Actual data
            // ...
            isDestroyed: false,
            unsubscribeFromMutations: null,
            colorUpdate: false
        };
    },
    computed: 
	{
	    node()
	    {
	        return this.$store.state.project.nodes.get(this.nodeid);
	    },
	    eraBarBounds()
	    {
	        if (this.dateStart && this.dateEnd && !this.dateStart && !this.dateEnd) 
	        {
	            console.log('no way this is happening');
	        }
	        return this.$refs.eraBar.getBoundingClientRect();
	    },
	    textLeftPadding()
	    {
	        if (!this.$refs.eraBar || !this.$refs.eraText) return 0;

	        let era = this.textBounds;
	        let text = this.calcTextBounds();
	        let intermediate = this.edgeBounds.left;//clamp(this.edgeBounds.left, 0, era.right - era.left);
	        return intermediate;
	    },
	    textRightPadding()
	    {
	        //if (!this.$refs.eraBar || !this.$refs.eraText) return 0;
	        return this.edgeBounds.right;
	    },
	    textBounds()
	    {
	        if (this.dateStart && !this.dateStart && this.dateEnd && !this.dateEnd && this.camera.yearToPixels)
	        {
	            console.log('wot');
	        }
	        let baseBound = {min: this.yearBound.min.x, max: this.yearBound.max.x};
	        let bound = {left: this.yearBound.min.x, right: this.yearBound.max.x};
	        const logging = false;
	        for(let i = 0; i < this.eras.length; ++i)
	        {
	            /** @type {Node} */
	            let era = this.eras[i];
	            if (era == this.node) continue;
	            if (!era.component) continue;
	            let eb = era.component.yearBound;
				
	            // If they don't intersect, early-out
	            if (eb.max.x < bound.left || eb.min.x > bound.right)
	            {
	                if (logging)
	                {
	                    console.log(`No intersection w/ ${era.name}`);
	                }
	                continue;
	            }
				
	            // If our bounds are totally encompassed by the other bounds, early out
	            if (eb.max.x >= bound.right && eb.min.x <= bound.left)
	            {
	                if (logging)
	                {
	                    console.log(`Totally encompassed by ${era.name}`);
	                }
	                continue;
	            }

	            // If our bounds totally encompass the other bounds...
	            if (eb.max.x <= bound.right && eb.min.x >= bound.left)
	            {
	                // Figure out where we have the most space and shrink ourselves to that
	                let spaceLeft = eb.min.x - bound.left;
	                let spaceRight = bound.right - eb.max.x;
	                if (logging)
	                {
	                    console.log(`Totally encompasses ${era.name}; ${spaceLeft}, ${spaceRight}`);
	                }
	                if (spaceLeft > spaceRight)
	                {
	                    if (logging)
	                    {
	                        console.log(`Totally encompasses ${era.name}; sliding right bound`);
	                    }
	                    // Slide our right boundary to match the other era's start
	                    bound.right = Math.min(bound.right, eb.min.x);
	                }
	                else
	                {
	                    if (logging)
	                    {
	                        console.log(`Totally encompasses ${era.name}; sliding left bound`);
	                    }
	                    // Slide our left boundary to match the other era's end
	                    bound.left = Math.max(bound.left, eb.max.x);
	                }
	                continue;
	            }

	            // If this era starts before our era...
	            if (eb.min.x < bound.left)
	            {
	                // Slide our left boundary to match the other era's end
	                bound.left = Math.max(bound.left, eb.max.x);
	            }
	            // Otherwise the era starts after the current era...
	            else
	            {
	                // Slide our right boundary to match the other era's start
	                bound.right = Math.min(bound.right, eb.min.x);
	                // This era starts after our era
	            }
	        }
			
	        const y2ms =  (365 * 24 * 60 * 60 * 1000);
	        bound.left = Math.abs(baseBound.min - bound.left) / y2ms * this.camera.yearToPixels;
	        bound.right = Math.abs(baseBound.max - bound.right) / y2ms * this.camera.yearToPixels;

	        //bound.left = 0;
	        //bound.right = 0;
	        return bound;
	    },
	    // Required for layout algorithm
	    bound() 
	    {
	        // Reactivity
	        this.startX;
	        this.dateEnd;
	        this.camera.yearToPixels;
	        this.camera.minDate;
	        return this.calculateBounds();
	    },

	    // Required for layout algorithm
	    yearBound() 
	    {
	        // Reactivity
	        if (this.dateStart && !this.dateStart && this.dateEnd && !this.dateEnd && this.camera.yearToPixels)
	        {
	            console.log('wot');
	        }
	        return this.calculateYearBounds();
	    },

	    // Convenience getters for camera properties
	    yearToPixels() 
	    {
	        return this.camera.yearToPixels;
	    },

	    cameraStartDate()
	    {
	        return this.camera.minDate;
	    },

	    cameraStartX() 
	    {
	        return this.cameraStartDate * this.yearToPixels;
	    },
		
	    // Convenience getters for node
	    eraPosition() 
	    {
	        this._fPosUpdate;
	        return this.node.position;
	    },

	    // CSS properties
	    startX() 
	    {
	        this._fPosUpdate;
	        this._fDateUpdate;
			
	        let pixelsPerYear = this.yearToPixels; // TODO: this should get passed directly but whatever
	        // 31556926 == seconds in a year
	        let pixelsPerSecond = pixelsPerYear / 31556926;
	        let secondsSinceEpoch = (this.dateStart.getTime() / 1000);
	        let secondsSinceYearZero = secondsSinceEpoch + 62167219200;

	        return (pixelsPerSecond * secondsSinceYearZero);
	    },
	    yPos() 
	    {
	        return (this.node.trackID - this.trackCnt / 2) * 50 + this.rootHeight / 2;
	    },
	    tailLength() 
	    {
	        let pixelsPerYear = this.yearToPixels; // TODO: this should get passed directly but whatever
	        let pixelsPerSecond = pixelsPerYear / 31556926;
	        let secondsSinceEpoch = (this.dateStart.getTime() / 1000);
	        let secondsSinceYearZero = secondsSinceEpoch + 62167219200;

	        return ((this.dateEnd.getTime() / 1000) - secondsSinceEpoch) * pixelsPerSecond;
	    },
	    bgColor()
	    {
	        return (this.colorUpdate) ? this.node.eraBackgroundColorString : this.node.eraBackgroundColorString;
	    }
	},
    watch: 
	{
	    bound()
	    {
	        // setTimeout(() =>
	        // {
	        if (this && this.edgeBounds)
	        {
	            if (this.fitty)
	                this.fitty.fit();

	            this.$recompute('edgeBounds');
	        }
	        // }, 0);
	    }
	},
    recomputed: 
	{
	    dateStart() 
	    {
	        this._fDateUpdate;
	        return this.node.dateStart;
	    },
	    dateEnd() 
	    {
	        this._fDateUpdate;
	        return this.node.dateEnd;
	    },
	    endYear()
	    {
	        return this.dateEnd.getUTCFullYear();
	    },
	    name() 
	    {
	        return this.node.name;
	    },
	    // Computed separately from textBounds rather than inline so that we don't have to recompute era
	    // overlap when the camera moves
	    edgeBounds()
	    {
	        let offset = {
	            left: 0,
	            right: 0
	        };

	        if (this.$refs.eraText && this.$refs.eraActualText)
	        {
	            // TODO: Figure out a clean way of restricting the bounds from going past the width of the container
	            let tbound = this.textBounds;
	            let cbound = this.$refs.eraText.getBoundingClientRect();
	            let realLeft =  (cbound.left + tbound.left);
	            let realRight = (cbound.right - tbound.right);
				
	            let textWidth = CalculateTextSize(this.node.name, 35).x;
	            let realLeftDiff = Math.max(0, (-realLeft) - textWidth);
	            let realRightDiff = -Math.min((window.innerWidth - (realRight - textWidth)), 0);
	            let textBounds = this.$refs.eraActualText;

	            offset.left = Math.max(realLeftDiff, 0);
	            offset.right = Math.min(realRightDiff, this.tailLength);
	        }
	        return offset;
	    },
	},
    mounted() 
    {
        this.$nextTick(() =>
        {
            this.fitty = fitty(this.$refs.eraActualText, {maxSize: 32, minSize: 5});
        });
        this.$set(this.node, 'component', this);
        this.forceUpdatePosition();
        this.forceUpdateDates();
		
        let lastStart = this.node.dateStart;
        let lastEnd = this.node.dateEnd;

        this.unsubscribeFromMutations = this.$store.subscribe((mutation, state) =>
        {
            if (mutation.type == Mutations.updateNodeColor)
            {
                this.colorUpdate = !this.colorUpdate;
            }
        });
		
        let loop = () =>
        {
            if (this.isDestroyed)
            {
                console.log('STOP!');
                return;
            }
			
            if (lastStart != this.node.dateStart || lastEnd != this.node.dateEnd)
            {
                lastStart = this.node.dateStart;
                lastEnd = this.node.dateEnd;
				
                this.forceUpdateDates();
            }
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
    },
    beforeDestroy()
    {
        if (this.unsubscribeFromMutations)
        {
            this.unsubscribeFromMutations();
        }
        this.isDestroyed = true;
    },
    methods: 
	{
	    calcTextBounds()
	    {
	        if (this.dateStart && this.dateEnd && !this.dateStart && !this.dateEnd) 
	        {
	            console.log('no way this is happening'); 
	        }
	        return this.$refs.eraText.getBoundingClientRect();
	    },
	    calculateYearBounds()
	    {
	        return {
	            min: {x: Math.floor(this.node.dateStart)},
	            max: {x: Math.floor(this.node.dateEnd)}
	        };
	    },
	    calculateBounds(ignoreText = false) 
	    {
	        let node = this.node;
	        if (!this.$refs.eraSubroot || !this.$refs.eraText || !this.$refs.eraBar)
	        {
	            return this.calculateYearBounds();
	        }

	        /** @type {DOMRect} */
	        let rect = (this).$refs.eraSubroot.getBoundingClientRect();
	        let rect2 = (this).$refs.eraText.getBoundingClientRect();
	        if (ignoreText)
	        {
	            rect2 = rect;
	        }
	        let rect3 = (this).$refs.eraBar.getBoundingClientRect();

	        return {
	            min: {
	                x: Math.min(rect.left, rect2.left, rect3.left)
	            },
	            max: {
	                x: Math.max(rect.right, rect2.right, rect3.right)
	            }
	        };
	    },
	    forceUpdatePosition() 
	    {
	        //this.$set(this, '_fPosUpdate', !this._fPosUpdate);
	        this._fPosUpdate = !this._fPosUpdate;
	    },
	    forceUpdateDates() 
	    {
	        //this.$set(this, '_fDateUpdate', !this._fDateUpdate);
	        this._fDateUpdate = !this._fDateUpdate;
	        //this.forceUpdatePosition();
	        this.$recompute('dateStart');
	        this.$recompute('dateEnd');
	        this.$emit('regen-layout');
	        this.$forceUpdate();
	    },
	    forceUpdateRender()
	    {
	        this.forceUpdatePosition();
	        this.forceUpdateDates();
	        this.$recompute('name');
	    },
	    onEraClick(ev) 
	    {
	        //console.log(`CLICK DETECTED FOR ${this.node.name}`);
	        //console.log(ev);
	        let tgNode = this.node;
	        if (ev && !(ev.clientX == 0 && ev.clientY == 0))
	        {
	            let elems = document.elementsFromPoint(ev.clientX, ev.clientY);
	            if (elems.length > 1)
	            {
	                console.log(`Current == ${this.$store.state.project.lastSelected ? this.$store.state.project.lastSelected.name : "none"}`);
	                for(let key in elems)
	                {
	                    let elem = elems[key];
	                    if (elem.className.includes('node-'))
	                    {
	                        console.log('found node, skipping era check');
	                        return;
	                    }

	                    if (elem.tagName && elem.tagName.toLowerCase() == "div" && elem.className.includes("era-bar"))
	                    {
	                        let tg = elem.parentElement.parentElement.__vue__.node;
	                        console.log(`Trying ${tg.name}`);
	                        if (!this.$store.state.project.lastSelected || (tg.nodeid != this.$store.state.project.lastSelected.nodeid))
	                        {
	                            console.log("pass :D");
	                            tgNode = tg;
	                            break;
	                        }
	                        //console.log(tgNode.name);
	                    }
	                }
	            }
	        }
	        if (!this.isCameraDragging)
	        {
	            //console.log(`SELECT ${tgNode.name}`);
	            this.$store.commit(Mutations.setSelected, tgNode);
	        }
	    }
	}
});
</script>

<style lang="scss" scoped>
$circle-radius: 64px;
$line-height: 32px;

.era-root
{
	z-index: 1;
	position: absolute;
	margin-left: -$circle-radius / 2;
	transition: filter 250ms;
}

.era-root:hover
{
	//transition-delay: .25s;
	cursor: pointer;
	filter: brightness(1.15);
}

.era-subroot
{
	//transition: left 100ms ease-out;
	//transition: left 500ms ease;
	transition: top 500ms ease;
	position: absolute;
}

.era-bar:focus
{
	transition: 200ms;
	box-shadow: 0 0 0 4pt #427ad6!important;
}

.era-bar
{
	//transition: 1s;
	position: absolute;
	height: $line-height;
	color: white;
	left: $circle-radius / 2;
}

.era-text
{
	display: inline-block;
	font-weight: 500;
	//width: max-content;
	text-align: center;
	//position: absolute;
	font-size: 30px;
	//left: $circle-radius / 2;
	color: white;
	// Trying to layer the white outline
    text-shadow:
		-1px -1px 0px rgba(16, 16, 16, .75),
		1px -1px 0px rgba(16, 16, 16, .75),
		1px 1px 0px rgba(16, 16, 16, .75),
		-1px 1px 0px rgba(16, 16, 16, .75),
		0 0 15px rgba(0,0,0,0.6);
		//0 0 1px white,
		//0 0 2px white,
		//0 0 2px white,
		//0 0 5px white,
		//0 0 20px rgba(255,255,255,0.4);
	;
	overflow: hidden;
}

.era-text-container
{
	position: absolute;
	z-index: 1;
}
</style>